import {
  WSActions,
  WSAlert,
  WSCard,
  WSControl,
  WSControlGroupCardOption,
  WSControlGroupCards,
  WSInfoBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  ExternalBankAccount,
  PayoutDestinationType,
  PreferredPayoutSpeed
} from "@wingspanhq/wingspan-api-sdk";
import { useCallback, useMemo, useState } from "react";
import { queriesInternalAccounts, queriesPayouts } from "@query";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { selectorExternalBankAccountInstitution } from "@selectors/selectorExternalBankAccountInstitution";
import { selectorExternalBankAccountName } from "@selectors/selectorExternalBankAccountName";
import { selectorExternalBankAccountRtpReady } from "@selectors/selectorExternalBankAccountRtpReady";
import { selectorPayoutSettingsInstantFee } from "@selectors/selectorPayoutSettingsInstantFee";
import {
  avatarBankAccount,
  avatarBankCard,
  avatarWallet
} from "../../../../shared/constants/avatars";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { selectorPendingBankingAccount } from "../../../../shared/selectors/selectorPendingBankingAccount";

type AlternativeSelection = "debit-card" | "try-another-bank" | "wallet";

type Props = {
  accounts: ExternalBankAccount[];
  onBack: () => void;
  onNext?: () => void;
  onDebitCard: () => void;
  onTryAnotherBank: () => void;
  onActivateWallet: () => void;
};

export const RouteInstantAccountSelect: React.FC<Props> = props => {
  const queryPayoutSettings = useQueryOwnPayoutSettings();
  const payoutSettingsId = queryPayoutSettings.data?.id;
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();

  const hasActiveWallet = (queryInternalAccounts.data?.data || []).some(
    selectorActiveBankingAccount
  );
  const hasPendingWallet = (queryInternalAccounts.data?.data || []).some(
    selectorPendingBankingAccount
  );
  const fee = selectorPayoutSettingsInstantFee(queryPayoutSettings.data);

  const [selectedAccountId, setSelectedAccountId] = useState<
    string | undefined
  >(props.accounts[0]?.id);
  const [alternativeOption, setAlternativeOption] = useState<
    AlternativeSelection | undefined
  >(undefined);
  const [useAsStandard, setUseAsStandard] = useState(false);

  const selectedAccount = useMemo(
    () => props.accounts.find(a => a.id === selectedAccountId),
    [props.accounts, selectedAccountId]
  );

  const isSelectedEligible = selectedAccount
    ? selectorExternalBankAccountRtpReady(selectedAccount)
    : true;

  const [updatePayoutSettings, updatePayoutSettingsMeta] =
    queriesPayouts.updatePayoutSettings.useMutation({
      onSuccess: props.onNext
    });

  const handleContinue = useCallback(() => {
    if (!selectedAccountId || !payoutSettingsId) return;

    if (isSelectedEligible) {
      updatePayoutSettings({
        payoutSettingsId,
        payoutSettingsUpdate: {
          preferredPayoutSpeed: PreferredPayoutSpeed.INSTANT,
          instantDestination: {
            id: selectedAccountId,
            type: PayoutDestinationType.EXTERNAL_BANK_ACCOUNT
          }
        }
      });
      return;
    }

    if (useAsStandard) {
      updatePayoutSettings({
        payoutSettingsId,
        payoutSettingsUpdate: {
          preferredPayoutSpeed: PreferredPayoutSpeed.STANDARD,
          standardDestination: {
            id: selectedAccountId,
            type: PayoutDestinationType.EXTERNAL_BANK_ACCOUNT
          }
        }
      });
      return;
    }

    if (alternativeOption === "debit-card") {
      props.onDebitCard();
    } else if (alternativeOption === "try-another-bank") {
      props.onTryAnotherBank();
    } else if (alternativeOption === "wallet") {
      if (hasActiveWallet) {
        const activeWallet = (queryInternalAccounts.data?.data || []).find(
          selectorActiveBankingAccount
        );
        if (activeWallet) {
          updatePayoutSettings({
            payoutSettingsId,
            payoutSettingsUpdate: {
              preferredPayoutSpeed: PreferredPayoutSpeed.INSTANT,
              instantDestination: {
                id: activeWallet.id,
                type: PayoutDestinationType.INTERNAL_ACCOUNT
              }
            }
          });
        }
      } else {
        props.onActivateWallet();
      }
    }
  }, [
    selectedAccountId,
    payoutSettingsId,
    isSelectedEligible,
    useAsStandard,
    alternativeOption,
    hasActiveWallet,
    queryInternalAccounts.data,
    updatePayoutSettings,
    props
  ]);

  const canContinue =
    isSelectedEligible || useAsStandard || !!alternativeOption;
  const isLoading = updatePayoutSettingsMeta.isLoading;

  const accountOptions = useMemo<WSControlGroupCardOption[]>(
    () =>
      props.accounts.map(account => ({
        header: {
          label: {
            text:
              selectorExternalBankAccountInstitution(account) ||
              account.accountName,
            secondaryText: selectorExternalBankAccountName(account),
            avatar: avatarBankAccount
          }
        },
        value: account.id
      })),
    [props.accounts]
  );

  return (
    <WSList gap="2XL">
      <WSCard
        header={{
          label: {
            text: "Set default payout method",
            helperText:
              "Select an account you added through Plaid as your payout method."
          }
        }}
      >
        <WSList gap="M">
          <WSControlGroupCards
            value={selectedAccountId ?? ""}
            onChange={newValue => {
              setSelectedAccountId(newValue);
              setAlternativeOption(undefined);
              setUseAsStandard(false);
            }}
            options={accountOptions}
          />

          {!isSelectedEligible && (
            <WSAlert
              theme="warning"
              icon="alert"
              title="Instant payout unavailable"
            >
              This account isn't eligible for instant payouts. Please select a
              different account or choose a standard payout method.
            </WSAlert>
          )}
        </WSList>
      </WSCard>

      {!isSelectedEligible && (
        <WSCard
          header={{
            label: {
              text: "Try another method"
            }
          }}
        >
          <WSList gap="2XL">
            <WSText.ParagraphSm color="gray500">
              Connect a debit card, use your Wingspan Wallet, try another bank,
              or switch to standard payout.
            </WSText.ParagraphSm>

            <WSControlGroupCards
              value={alternativeOption ?? ""}
              onChange={newValue => {
                setAlternativeOption(newValue as AlternativeSelection);
                setUseAsStandard(false);
              }}
              options={[
                {
                  header: {
                    label: {
                      text: `Connect a debit card (${fee}% fee)`,
                      helperText:
                        "Receive payments by connecting an eligible Visa or Mastercard debit card",
                      avatar: avatarBankCard
                    }
                  },
                  value: "debit-card"
                },
                {
                  header: {
                    label: {
                      text: hasActiveWallet
                        ? `Wingspan Wallet (${fee}% fee)`
                        : `Activate Wingspan Wallet (${fee}% fee)`,
                      helperText: hasActiveWallet
                        ? "Use your Wingspan Wallet as your instant payout method"
                        : "Business bank account with instant access to your funds",
                      avatar: avatarWallet
                    },
                    ...(hasPendingWallet
                      ? {
                          value: {
                            pill: {
                              theme: "blue" as const,
                              text: "Pending activation",
                              badge: true,
                              type: "noBackground" as const
                            },
                            stacked: true
                          }
                        }
                      : {})
                  },
                  disabled: hasPendingWallet,
                  value: "wallet"
                },
                {
                  header: {
                    label: {
                      text: "Try another bank",
                      helperText:
                        "Try instant payout by connecting an account from another bank",
                      avatar: avatarBankAccount
                    }
                  },
                  value: "try-another-bank"
                }
              ]}
            />

            <WSInfoBox theme="info">
              <WSText.ParagraphXs mb="L" color="gray500">
                If you wish to use the bank account you provided as a{" "}
                <b>standard payout method</b> instead, check the box below.
              </WSText.ParagraphXs>

              <WSControl
                type="checkbox"
                value={useAsStandard}
                onChange={checked => {
                  setUseAsStandard(checked);
                  if (checked) {
                    setAlternativeOption(undefined);
                  }
                }}
                label="Add this bank account and receive you funds in the standard 2-5 business days."
                size="S"
              />
            </WSInfoBox>
          </WSList>
        </WSCard>
      )}

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Back",
            kind: "Tertiary",
            onClick: props.onBack
          },
          {
            label: "Continue",
            disabled: !canContinue,
            loading: isLoading,
            onClick: handleContinue
          }
        ]}
      />
    </WSList>
  );
};
